
import { useCallback } from "react";
import { ArticleFormData } from "@/types/article-types";
import { useArticleOperations } from "./useArticleOperations";
import { useTagOperations } from "./useTagOperations";
import { useMediaOperations } from "./useMediaOperations";
import { useAuthorOperations } from "./useAuthorOperations";
import { useToast } from "@/components/ui/use-toast";

export const useArticleActions = () => {
  const { toast } = useToast();
  
  const { 
    createArticle: createArticleBase, 
    updateArticle: updateArticleBase, 
    deleteArticle: deleteArticleBase,
    fetchArticleById: fetchArticleByIdBase
  } = useArticleOperations();

  const { createTag } = useTagOperations();

  const { 
    uploadMediaToStorage, 
    fetchMedia, 
    updateMediaMetadata, 
    deleteMedia 
  } = useMediaOperations();
  
  const {
    createAuthor,
    updateAuthor,
    deleteAuthor
  } = useAuthorOperations();

  // Enhanced article operations with better error handling
  const createArticleWrapper = useCallback(async (formData: ArticleFormData) => {
    try {
      console.log("Creating article with data:", formData);
      const result = await createArticleBase(formData);
      if (!result) {
        throw new Error("Failed to create article");
      }
      return result;
    } catch (error: any) {
      console.error("Error in createArticleWrapper:", error);
      toast({
        variant: "destructive",
        title: "Failed to create article",
        description: error.message || "An unknown error occurred"
      });
      throw error;
    }
  }, [createArticleBase, toast]);
  
  const updateArticleWrapper = useCallback(async (id: string, formData: ArticleFormData) => {
    try {
      console.log("Updating article with ID:", id, "and data:", formData);
      const result = await updateArticleBase(id, formData);
      if (!result) {
        throw new Error("Failed to update article");
      }
      return result;
    } catch (error: any) {
      console.error("Error in updateArticleWrapper:", error);
      toast({
        variant: "destructive",
        title: "Failed to update article",
        description: error.message || "An unknown error occurred"
      });
      throw error;
    }
  }, [updateArticleBase, toast]);

  // Add wrapper for fetchArticleById
  const fetchArticleByIdWrapper = useCallback(async (id: string) => {
    try {
      console.log("Fetching article with ID:", id);
      const result = await fetchArticleByIdBase(id);
      return result;
    } catch (error: any) {
      console.error("Error in fetchArticleByIdWrapper:", error);
      toast({
        variant: "destructive",
        title: "Failed to fetch article",
        description: error.message || "An unknown error occurred"
      });
      return null;
    }
  }, [fetchArticleByIdBase, toast]);

  // Enhanced media upload with metadata and better error handling
  const uploadMediaWrapper = useCallback(async (file: File, options = {}) => {
    try {
      console.log("Uploading media:", file.name);
      const result = await uploadMediaToStorage(file, 'articles', options);
      if (!result) {
        throw new Error("Failed to upload media");
      }
      return result;
    } catch (error: any) {
      console.error("Error in uploadMediaWrapper:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "Failed to upload media"
      });
      throw error; // Re-throw to allow proper error handling in the caller
    }
  }, [uploadMediaToStorage, toast]);

  return {
    createArticle: createArticleWrapper,
    updateArticle: updateArticleWrapper,
    deleteArticle: deleteArticleBase,
    createTag,
    uploadMediaToStorage: uploadMediaWrapper,
    fetchMedia,
    updateMediaMetadata,
    deleteMedia,
    createAuthor,
    updateAuthor,
    deleteAuthor,
    fetchArticleById: fetchArticleByIdWrapper
  };
};
